import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  clearLegacyListingDecisions,
  readLegacyListingDecisions,
} from '../_data/listing-decisions'
import { ListingTag, ListingTagOrNull } from '../_types/listing-tag'
import { useAuth } from './use-auth'
import { useQuickPassProfile } from './use-quickpass-profile'
import {
  ListingDecisionsContextValue,
  ListingDecisionsProviderProps,
} from './listing-decisions-context-types'
import { ListingDecisionsContext } from './use-listing-decisions'

const isMapEmpty = <V,>(map: Record<string, V>): boolean => {
  return Object.keys(map).length === 0
}

/**
 * Provides per-listing tag + note state, sourced from the renter's
 * profile so the data syncs across devices. Setters call patchProfile
 * which writes through the existing profile-update path. Any legacy
 * device-local tags + notes are folded forward on first profile load.
 *
 * @param {ListingDecisionsProviderProps} props
 * @returns {JSX.Element}
 */
const ListingDecisionsProvider = ({
  children,
}: ListingDecisionsProviderProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { profile, patchProfile } = useQuickPassProfile()
  const hasMigratedReference = useRef<string>('')

  useEffect(() => {
    if (quickPassUser === null || profile === null) {
      return
    }

    if (hasMigratedReference.current === quickPassUser.id) {
      return
    }

    hasMigratedReference.current = quickPassUser.id

    const profileTagsEmpty = isMapEmpty(profile.listingTags ?? {})
    const profileNotesEmpty = isMapEmpty(profile.listingNotes ?? {})
    const legacy = readLegacyListingDecisions(quickPassUser.id)

    const shouldMigrateTags =
      profileTagsEmpty === true && isMapEmpty(legacy.tags) === false
    const shouldMigrateNotes =
      profileNotesEmpty === true && isMapEmpty(legacy.notes) === false

    if (shouldMigrateTags === true || shouldMigrateNotes === true) {
      const patch: {
        listingTags?: Record<string, ListingTag>
        listingNotes?: Record<string, string>
      } = {}

      if (shouldMigrateTags === true) {
        patch.listingTags = legacy.tags
      }
      if (shouldMigrateNotes === true) {
        patch.listingNotes = legacy.notes
      }

      void patchProfile(patch)
    }

    if (
      isMapEmpty(legacy.tags) === false ||
      isMapEmpty(legacy.notes) === false
    ) {
      clearLegacyListingDecisions(quickPassUser.id)
    }
  }, [quickPassUser, profile, patchProfile])

  const tags = useMemo<Record<string, ListingTag>>(
    () => profile?.listingTags ?? {},
    [profile],
  )
  const notes = useMemo<Record<string, string>>(
    () => profile?.listingNotes ?? {},
    [profile],
  )

  const setListingTag = useCallback(
    (listingId: string, tag: ListingTagOrNull): void => {
      if (profile === null) {
        return
      }

      const next: Record<string, ListingTag> = {
        ...(profile.listingTags ?? {}),
      }

      if (tag === null) {
        delete next[listingId]
      } else {
        next[listingId] = tag
      }

      void patchProfile({ listingTags: next })
    },
    [profile, patchProfile],
  )

  const setListingNote = useCallback(
    (listingId: string, note: string): void => {
      if (profile === null) {
        return
      }

      const next: Record<string, string> = { ...(profile.listingNotes ?? {}) }

      if (note.trim() === '') {
        delete next[listingId]
      } else {
        next[listingId] = note
      }

      void patchProfile({ listingNotes: next })
    },
    [profile, patchProfile],
  )

  const value = useMemo<ListingDecisionsContextValue>(
    () => ({
      tags,
      notes,
      setListingTag,
      setListingNote,
    }),
    [tags, notes, setListingTag, setListingNote],
  )

  return (
    <ListingDecisionsContext.Provider value={value}>
      {children}
    </ListingDecisionsContext.Provider>
  )
}

export { ListingDecisionsProvider }
